<script setup lang="ts">
import type {
  NavigationPageItem,
  NavigationPageItemVariant,
  NavigationPageScale,
} from "../../../types/blocks/navigation/NavigationPageOption";
import {
  editorDisplayStates,
  editorLayers,
  findDisplayState,
  resolveLayers,
  variantStateId,
} from "./editorResolve";
import {
  NAV_FIELDS,
  formatValue,
  getField,
  isSet,
  seedFieldValue,
  setField,
  summarizeSetFields,
  unsetField,
  type FieldDef,
} from "./fieldRegistry";
import FieldSourceTable from "./FieldSourceTable.vue";
import LayerCard from "./LayerCard.vue";
import LayerEditRow from "./LayerEditRow.vue";
import LayerFieldRow from "./LayerFieldRow.vue";
import MiniLayerRow from "./MiniLayerRow.vue";
import ResolvedCardPreview from "./ResolvedCardPreview.vue";
import SetFieldMenu from "./SetFieldMenu.vue";

const props = defineProps<{
  blockId: string;
  itemIndex: number;
  open: boolean;
}>();

const emit = defineEmits<{
  "update:open": [value: boolean];
  "open-variant": [variantIndex: number];
}>();

const { t } = useI18n({ useScope: "local" });
const { block, field, patch } = useInlineBlockEditor(props.blockId);

const dialogOpen = computed({
  get: () => props.open,
  set: (value: boolean) => emit("update:open", value),
});

const scale = field<NavigationPageScale>("scale", "md");
const items = computed<NavigationPageItem[]>(() =>
  Array.isArray(block.value?.items) ? block.value.items : [],
);
const displayStates = computed(() => editorDisplayStates(block.value?.displayStates));
const item = computed(() => items.value[props.itemIndex]);
const defaultDisplayStateId = computed(
  () => displayStates.value.find((state) => state.id === "default")?.id
    ?? displayStates.value[0]?.id
    ?? "default",
);
const variants = computed<NavigationPageItemVariant[]>(() =>
  Array.isArray(item.value?.variants) ? item.value.variants : [],
);
const previewVariantIndex = shallowRef<number | null>(null);
const simulatedVariant = computed(() =>
  previewVariantIndex.value === null
    ? null
    : variants.value[previewVariantIndex.value] ?? null,
);
const layers = computed(() =>
  item.value
    ? editorLayers(item.value, displayStates.value, simulatedVariant.value)
    : [],
);
const resolvedState = computed(() => resolveLayers(layers.value));
const baseState = computed(() =>
  item.value
    ? findDisplayState(displayStates.value, item.value.displayStateId)
    : null,
);
const baseStateFields = computed(() =>
  baseState.value
    ? NAV_FIELDS
      .filter((field) => isSet(getField(baseState.value, field)))
      .map((field) => ({
        key: field.key,
        label: field.label,
        value: formatValue(field, getField(baseState.value, field)),
        mono: field.kind === "mono" || field.kind === "path" || field.kind === "image",
        overriddenBy: overriddenBy(field, "base"),
      }))
    : [],
);
const linkSetFields = computed(() =>
  item.value
    ? NAV_FIELDS.filter((field) => isSet(getField(item.value, field)))
    : [],
);
const linkUnsetFields = computed(() =>
  item.value
    ? NAV_FIELDS.filter((field) => !isSet(getField(item.value, field)))
    : [],
);
const sourceRows = computed(() =>
  NAV_FIELDS
    .filter((field) => isSet(getField(resolvedState.value.resolved, field)))
    .map((field) => ({
      label: field.label,
      value: formatValue(field, getField(resolvedState.value.resolved, field)),
      mono: field.kind === "mono" || field.kind === "path" || field.kind === "image",
      source: sourceLabel(resolvedState.value.winner[field.key]?.key),
      sourceTone: resolvedState.value.winner[field.key]?.key === "base"
        ? "base"
        : resolvedState.value.winner[field.key]?.key === "state"
          ? "state"
          : resolvedState.value.winner[field.key]?.key === "variant"
            ? "variant"
            : "link",
    })),
);

watch(
  () => props.open,
  (open) => {
    if (open) previewVariantIndex.value = null;
  },
);

watch(
  item,
  (value) => {
    if (!value && props.open) emit("update:open", false);
  },
);

function cloneVariant(variant: NavigationPageItemVariant): NavigationPageItemVariant {
  return {
    ...variant,
    conditions: [...(variant.conditions ?? [])],
    classes: variant.classes ? { ...variant.classes } : undefined,
  };
}

function cloneItem(entry: NavigationPageItem): NavigationPageItem {
  return {
    ...entry,
    classes: entry.classes ? { ...entry.classes } : undefined,
    variants: Array.isArray(entry.variants) ? entry.variants.map(cloneVariant) : [],
  };
}

function updateCurrentItem(
  transform: (draft: NavigationPageItem) => NavigationPageItem,
) {
  const current = item.value;
  if (!current) return;

  patch({
    items: items.value.map((entry, index) =>
      index === props.itemIndex ? transform(cloneItem(entry)) : entry),
  });
}

function setBaseState(value: unknown) {
  updateCurrentItem((draft) => ({
    ...draft,
    displayStateId: String(value || defaultDisplayStateId.value),
  }));
}

function stateName(stateId: string | undefined): string {
  return findDisplayState(displayStates.value, stateId).name;
}

function sourceLabel(key: "base" | "link" | "state" | "variant" | undefined) {
  if (key === "base") return "Base state";
  if (key === "state") return "State";
  if (key === "variant") return "Variant";
  return "Link";
}

function overriddenBy(def: FieldDef, layerKey: "base" | "link") {
  const winner = resolvedState.value.winner[def.key];
  if (!winner || winner.key === layerKey) return null;
  return `overridden by ${sourceLabel(winner.key).toLowerCase()}`;
}

function writeField(def: FieldDef, value: unknown) {
  updateCurrentItem((draft) => setField(draft, def, value));
}

function removeField(def: FieldDef) {
  updateCurrentItem((draft) => unsetField(draft, def));
}

function previewSelectValue() {
  return previewVariantIndex.value === null
    ? "base"
    : `variant:${previewVariantIndex.value}`;
}

function setPreview(value: unknown) {
  const text = String(value ?? "base");
  previewVariantIndex.value = text.startsWith("variant:")
    ? Number(text.split(":")[1] ?? -1)
    : null;
}

function conditionSummary(variant: NavigationPageItemVariant): string {
  const count = variant.conditions?.length ?? 0;
  if (!count) return t("variants.always");
  return t("variants.conditionSummary", {
    count,
    composition: variant.composition === "any" ? t("variants.any") : t("variants.all"),
  });
}

function addVariant() {
  const nextIndex = variants.value.length;
  updateCurrentItem((draft) => ({
    ...draft,
    variants: [
      ...(draft.variants ?? []),
      {
        id: crypto.randomUUID(),
        name: t("variants.defaultName", { number: nextIndex + 1 }),
        composition: "all",
        conditions: [],
        displayStateId: item.value?.displayStateId ?? defaultDisplayStateId.value,
        classes: {},
      },
    ],
  }));
  emit("open-variant", nextIndex);
}

function seedField(def: FieldDef) {
  writeField(def, seedFieldValue(def, getField(resolvedState.value.resolved, def)));
}
</script>

<template>
  <Dialog v-model:open="dialogOpen">
    <DialogContent
      v-if="item"
      class="grid-rows-[auto_minmax(0,1fr)_auto] max-h-[88dvh] gap-0 p-0 sm:max-w-[1150px]"
    >
      <DialogHeader class="border-b px-6 py-3.5 text-left">
        <DialogTitle class="space-y-2">
          <div class="text-[10px] font-bold uppercase tracking-[0.08em] text-[var(--cat-navigation)]">
            {{ t("eyebrow", { index: itemIndex + 1, count: items.length }) }}
          </div>
          <div class="flex flex-wrap items-center gap-3">
            <Input
              :model-value="item.title ?? ''"
              class="h-8 w-[280px] rounded-sm bg-background text-[13.5px] font-semibold"
              :placeholder="t('titlePlaceholder')"
              @update:model-value="(value) => writeField({ key: 'title', label: 'Title', kind: 'text' }, value)"
            />
            <p class="text-[11.5px] font-normal text-muted-foreground">
              {{ t("caption") }}
            </p>
          </div>
        </DialogTitle>
        <DialogDescription class="sr-only">
          {{ t("description") }}
        </DialogDescription>
      </DialogHeader>

      <div class="grid min-h-0 grid-cols-[minmax(0,1fr)_396px]">
        <div class="min-h-0 overflow-y-auto px-6 py-4">
          <div class="mb-4 flex items-start gap-2 rounded-lg border bg-card px-3 py-2">
            <Icon mode="svg" size="14" name="lucide:layers" class="mt-0.5 text-[var(--cat-navigation)]" />
            <p class="text-[11.5px] leading-snug text-muted-foreground">
              <strong class="font-semibold text-foreground">{{ t("introStrong") }}</strong>
              {{ t("introBody") }}
            </p>
          </div>

          <div class="space-y-3">
            <LayerCard
              icon="lucide:swatch-book"
              :title="stateName(item.displayStateId)"
              badge="BASE"
              badge-tone="base"
              :subtitle="t('layers.baseStateSub')"
            >
              <template #headerRight>
                <Select
                  :model-value="item.displayStateId ?? defaultDisplayStateId"
                  @update:model-value="setBaseState"
                >
                  <SelectTrigger class="h-7 w-[150px] rounded-sm bg-background text-[12px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem
                      v-for="state in displayStates"
                      :key="state.id"
                      :value="state.id"
                    >
                      {{ state.name }}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </template>

              <LayerFieldRow
                v-for="field in baseStateFields"
                :key="field.key"
                :label="field.label"
                :value="field.value"
                :mono="field.mono"
                :overridden-by="field.overriddenBy"
              />
            </LayerCard>

            <LayerCard
              icon="lucide:pencil"
              :title="t('layers.thisLink')"
              badge="EDITED HERE"
              badge-tone="link"
              :indent="1"
              :subtitle="t('layers.thisLinkSub')"
            >
              <LayerEditRow
                v-for="def in linkSetFields"
                :key="def.key"
                :def="def"
                :model-value="getField(item, def)"
                @update:model-value="(value) => writeField(def, value)"
                @unset="removeField(def)"
              />
              <p v-if="!linkSetFields.length" class="text-[11px] text-muted-foreground">
                {{ t("layers.linkEmpty") }}
              </p>
              <SetFieldMenu
                tone="link"
                :available="linkUnsetFields"
                :label="t('layers.setAnotherField')"
                @pick="seedField"
              />
            </LayerCard>

            <LayerCard
              icon="lucide:git-branch"
              :title="t('layers.variants')"
              badge="FIRST MATCH WINS"
              badge-tone="variant"
              :indent="2"
              :subtitle="t('layers.variantsSub')"
            >
              <div class="space-y-1.5">
                <MiniLayerRow
                  v-for="(variant, index) in variants"
                  :key="variant.id"
                  :chip="`V${index + 1}`"
                  tone="variant"
                  :title="variant.name || t('variants.untitled', { number: index + 1 })"
                  :meta="`${conditionSummary(variant)} · ${stateName(variantStateId(variant))}`"
                  @click="emit('open-variant', index)"
                />
                <div
                  v-if="!variants.length"
                  class="rounded-md border border-dashed px-2 py-2 text-[10.5px] text-muted-foreground"
                >
                  {{ t("variants.empty") }}
                </div>
                <Button
                  type="button"
                  variant="outline"
                  class="h-[28px] w-full justify-center rounded-md border-dashed text-[10.5px] font-semibold"
                  @click="addVariant"
                >
                  <Icon mode="svg" size="12" name="lucide:plus" class="mr-1" />
                  {{ t("variants.add") }}
                </Button>
              </div>
            </LayerCard>
          </div>
        </div>

        <div class="min-h-0 overflow-y-auto border-l bg-[var(--canvas)] px-5 py-4">
          <div class="space-y-4">
            <EditorField :label="t('preview.label')">
              <Select :model-value="previewSelectValue()" @update:model-value="setPreview">
                <SelectTrigger class="h-8 rounded-sm bg-background text-[12.5px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="base">{{ t("preview.base") }}</SelectItem>
                  <SelectItem
                    v-for="(variant, index) in variants"
                    :key="variant.id"
                    :value="`variant:${index}`"
                  >
                    {{ variant.name || t("variants.untitled", { number: index + 1 }) }}
                  </SelectItem>
                </SelectContent>
              </Select>
            </EditorField>

            <ResolvedCardPreview :resolved="resolvedState.resolved" :scale="scale" />
            <FieldSourceTable :rows="sourceRows" />
          </div>
        </div>
      </div>

      <DialogFooter class="items-center border-t px-6 py-4 sm:justify-between">
        <p class="text-[11px] text-muted-foreground">
          {{ t("footer") }}
        </p>
        <Button type="button" @click="dialogOpen = false">{{ t("done") }}</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>

<i18n lang="yaml">
en:
  eyebrow: "NAVIGATION PAGE · LINK {index} OF {count}"
  titlePlaceholder: "Link title"
  caption: "The link's own fields sit on top of its display state. Variants take over when their conditions match."
  description: "Edit the link layers, preview the resolved card, and inspect field sources."
  introStrong: "The card builds in layers."
  introBody: "The link only stores what it changes — everything else comes from its display state."
  layers:
    baseStateSub: "Shared look"
    thisLink: "This link"
    thisLinkSub: "Only the fields set here override the base state."
    linkEmpty: "This link sets nothing yet — it shows the display state as-is."
    setAnotherField: "Set another field"
    variants: "State variants"
    variantsSub: "First matching variant wins."
  variants:
    add: "Add variant"
    empty: "No variants — always shows the base look."
    untitled: "Variant {number}"
    always: "Always"
    all: "all"
    any: "any"
    conditionSummary: "{count} conditions · {composition}"
    defaultName: "State {number}"
  preview:
    label: "Preview as"
    base: "Base look"
  footer: "Use the preview picker to simulate each variant. Edits apply live."
  done: "Done"
</i18n>
